import { Money, PaymentInfo, PaymentInfoStatus, PaymentTypeEnum, Quote } from "../Types";
import { FormatCurrency, FormattedMoney } from "../Helpers/MoneyHelper";

const PAID_IN_FULL_PAYMENT_TYPES: ReadonlySet<PaymentTypeEnum> = new Set([
  PaymentTypeEnum.PAID_IN_FULL,
  PaymentTypeEnum.PAID_IN_FULL_WITH_DISCOUNT,
]);

/**
 * Payment used for pay-in-full discount breakdown and header "charged" total when a discount applied.
 * Only considers paid {@link PaymentTypeEnum.PAID_IN_FULL} / {@link PaymentTypeEnum.PAID_IN_FULL_WITH_DISCOUNT}
 * rows (newest with non-zero {@link PaymentInfo.appliedDiscount} when present). Returns `undefined` if none exist,
 * so discount UI does not use unrelated paid installments.
 */
export function getPaymentForPaidInFullDiscountRow(payments?: Array<PaymentInfo> | null): PaymentInfo | undefined {
  if (!payments?.length) {
    return undefined;
  }
  const paidInFull = payments.filter(
    (p) => p.status === PaymentInfoStatus.Paid && p.paymentType != null && PAID_IN_FULL_PAYMENT_TYPES.has(p.paymentType)
  );
  if (paidInFull.length === 0) {
    return undefined;
  }
  paidInFull.sort((a, b) => new Date(b.madeAt).getTime() - new Date(a.madeAt).getTime());
  const withNonZeroAppliedDiscount = paidInFull.find((p) => {
    if (p.appliedDiscount == null) return false;
    const n = Number(p.appliedDiscount);
    return !Number.isNaN(n) && Math.abs(n) > 0;
  });
  return withNonZeroAppliedDiscount ?? paidInFull[0];
}

function paymentHasNonZeroAppliedDiscount(payment: PaymentInfo | undefined): boolean {
  const raw = payment?.appliedDiscount;
  if (raw == null) return false;
  const n = Number(raw);
  return !Number.isNaN(n) && Math.abs(n) > 0;
}

/** Same rule as quote header: show amount actually charged when pay-in-full checkout applied a catalogue discount. */
export function shouldUseChargedAmountForPaidInFullQuoteTotal(payment: PaymentInfo | undefined): boolean {
  if (!payment || payment.status !== PaymentInfoStatus.Paid) return false;
  if (!paymentHasNonZeroAppliedDiscount(payment)) return false;
  return (
    payment.paymentType === PaymentTypeEnum.PAID_IN_FULL ||
    payment.paymentType === PaymentTypeEnum.PAID_IN_FULL_WITH_DISCOUNT
  );
}

/**
 * Formatted total shown next to "Total" on quote details (header + BookNow): catalogue total, or charged amount when
 * {@link shouldUseChargedAmountForPaidInFullQuoteTotal} applies.
 */
export function formatQuoteTotalAsShownInQuoteHeader(totalPrice: Money, payments?: Array<PaymentInfo> | null): string {
  const payInFull = getPaymentForPaidInFullDiscountRow(payments);
  if (shouldUseChargedAmountForPaidInFullQuoteTotal(payInFull)) {
    return FormatCurrency(payInFull!.payment.amount, totalPrice.currency);
  }
  return FormattedMoney(totalPrice);
}

export function getPaymentOrReservation(payments?: Array<PaymentInfo> | null): PaymentInfo | undefined {
  if (!payments) {
    return undefined;
  }
  const sortedPayments = [...payments].sort((a, b) => new Date(b.madeAt).getTime() - new Date(a.madeAt).getTime());
  return sortedPayments.find((x) => x.status === PaymentInfoStatus.Paid) ?? sortedPayments[0];
}

export function isQuoteFullyPaid(payments?: Array<PaymentInfo> | null): boolean {
  return getPaymentOrReservation(payments)?.status === PaymentInfoStatus.Paid;
}

export function getQuotePaymentStatus(quote?: Quote): PaymentInfoStatus | undefined {
  const paymentInfo = getPaymentOrReservation(quote?.payments);
  if (!paymentInfo) {
    return undefined;
  }
  return paymentInfo.status;
}
